import { createPinia } from 'pinia';
import { createApp } from 'vue';

import { fetchAuthConfig } from './api/auth.js';
import { api, setOn401Handler } from './api/client.js';
import App from './App.vue';
import { installAuthGuard, router } from './router/index.js';
import { useAuthStore } from './stores/auth.js';
import { useTenantStore } from './stores/tenant.js';
import { isBrowserSupported } from './utils/featureDetect.js';

import './styles/tokens.css';
import './styles/reset.css';
import './styles/base.css';
import './styles/otera-components.css';
import './styles/otera-mockups.css';

if (!isBrowserSupported()) {
  document.documentElement.lang = 'en';
  document.documentElement.setAttribute('data-theme', 'dark');
  document.body.innerHTML = `
    <div style="
      min-height:100vh;display:flex;align-items:center;justify-content:center;
      padding:2rem;background:#0A0A0B;color:#F5F5F7;
      font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;">
      <section style="
        max-width:32rem;padding:3rem;border-radius:12px;
        background:#131317;border:1px solid #26262B;">
        <p style="margin:0 0 0.5rem;color:#A8A8AE;font-size:0.8125rem;
          letter-spacing:0.04em;text-transform:uppercase;">Otera</p>
        <h1 style="margin:0 0 1rem;font-size:1.5rem;line-height:1.2;">
          Your browser isn't supported.</h1>
        <p style="margin:0;color:#A8A8AE;line-height:1.5;">
          Please upgrade to the latest version of Chrome, Firefox, Safari,
          or Edge to use the dashboard.</p>
      </section>
    </div>`;
} else {
  const app = createApp(App);
  const pinia = createPinia();
  app.use(pinia);

  const auth = useAuthStore();
  const tenant = useTenantStore();
  installAuthGuard(router, auth);

  // Story 5.4 — fetch deployment config (smtpEnabled) ASAP. Fire-and-
  // forget; el guard de smtpGated routes se queda en stand-by hasta
  // que se resuelva (smtpEnabled === null = no-op del guard).
  auth.fetchConfig(fetchAuthConfig).catch(() => { /* swallowed in action */ });

  // Centralised 401 handler — clear local session state, abort in-flight
  // tenant fetches, and redirect to /login with a return_to so the user
  // lands back where they were after re-auth.
  setOn401Handler(() => {
    const path = router.currentRoute.value.fullPath;
    auth.clear();
    tenant.abortAll('session-expired');
    if (router.currentRoute.value.name !== 'login') {
      router.push({ path: '/login', query: { return_to: path } });
    }
  });

  app.use(router);

  // Rehidratación de sesión — al refrescar la página el estado Pinia
  // se pierde, pero el cookie `session_id` (HttpOnly) sobrevive.
  // `loadSession` consulta GET /auth/session: si el cookie sigue
  // siendo válido, repuebla el usuario y NO se pide login de nuevo.
  // Montamos DESPUÉS de que la prueba resuelva para que el router
  // guard vea el `isAuthenticated` correcto en la primera navegación
  // (si montáramos antes, el guard redirige a /login por defecto).
  auth.loadSession(api).finally(() => {
    // Sembramos el tenant activo igual que hace LoginView tras login:
    // sin esto los hero panels no tendrían appId al restaurar sesión.
    const first = auth.tenants[0];
    if (first && !tenant.activeAppId) tenant.init(first.app_id);
    app.mount('#app');
  });
}
